// What a card offers, and where (docs/design/actions.md, variant A as refined): quiet head icons
// (⧉ open in a panel, ↗ its board, + new item here / add sub-item, link, … more), one primary button
// (the next step of its flow; a split button in the deploy and verify queues, ▾ = Send back), the
// reaction and Claim chips, and everything else in the "…" menu, grouped Work / Organize / Admin.
// Which actions a kind has is kindActions'; who may use them is can()'s; this only places them.
// Pure: no React, no DOM - Card.tsx draws it and the tests read it.

import {
  branchName,
  assigneeNames,
  assigneesOf,
  can,
  canAssignItem,
  canEditItem,
  canSetDraft,
  canSetDue,
  canVerify,
  childCount,
  closeLabel,
  flowLane,
  githubRef,
  kindActions,
  kindFlow,
  missingSteps,
  nameList,
  type BoardItem,
  type BoardLink,
  type BoardPr,
  type CardAction as KindAction,
  type Stage,
  type Viewer,
} from "@boem/core/shared";
import type { IconName } from "./components/Icons.tsx";

/** What a card's controls ask the view to do (LogView runs them: the same api calls and dialogs as before). */
export type CardAction =
  | { kind: "react"; value: "agree" | "question" | null }
  | { kind: "claim" }
  | { kind: "unclaim" }
  /** who has it now (OEM-285: the whole list, the lead first; empty: nobody); `note` says why and what the next step is for them */
  | { kind: "assignTo"; ids: string[]; note?: string }
  | { kind: "note" }
  /** edit one of your own notes (the Notes dialog, with it open) */
  | { kind: "editNote"; noteId: string }
  | { kind: "close" }
  | { kind: "reopen" }
  | { kind: "nondev"; value: boolean }
  /** make it a draft, or ready for the team (OEM-286) */
  | { kind: "draft"; value: boolean }
  | { kind: "teamsres"; value: boolean }
  | { kind: "history" }
  | { kind: "shot"; index: number }
  | { kind: "attach"; files?: File[] }
  | { kind: "removeShot"; id: string }
  | { kind: "releaseShot"; id: string }
  | { kind: "stage"; to: Stage }
  | { kind: "archive"; value: boolean }
  | { kind: "organize" }
  /** change its title and description (whoever logged it, and leads) */
  | { kind: "edit" }
  /** a new item under this one: in this project, or a sub-item */
  | { kind: "addChild" }
  /** open a project's board */
  | { kind: "openProject" }
  /** show an item (this one, or one of its sub-items or links) in a panel beside the board */
  | { kind: "panel"; num: number }
  /** this item's notes, the whole thread, in a panel beside the board */
  | { kind: "notesPanel" }
  /** go to an item on its board (a project's, or the main one) and scroll to it */
  | { kind: "goto"; num: number }
  /** the project chip: show only that project's items here (the board's Project filter); again: everything */
  | { kind: "filterProject"; num: number }
  /** Add link: it blocks, relates to or duplicates another item */
  | { kind: "link" }
  /** remove one of its links */
  | { kind: "unlink"; link: BoardLink }
  /** Copy to my board: a copy of it on the viewer's own board (orgs.md 1.5; from OEMUAP, 6.2) */
  | { kind: "copyToMine" }
  /** a personal item and OEMUAP (orgs.md 6): link it to an OEMUAP item, create one from it, unlink */
  | { kind: "bridgeLink" }
  | { kind: "bridgeCreate" }
  | { kind: "bridgeUnlink" }
  /** Send updates to the linked OEMUAP item (orgs.md 6.3) */
  | { kind: "bridgeSend" }
  /** GitHub (docs/github.md, OEMUAP only): copy the branch name, the reference or the PR description */
  | { kind: "copyText"; text: string; what: string; pr?: boolean }
  /** link a PR to it by its address, or unlink one */
  | { kind: "linkPr" }
  | { kind: "unlinkPr"; pr: BoardPr }
  /** a project's settings: its repos and how they deploy (leads) */
  | { kind: "projectSettings" }
  /** its due date (OEM-267): the dialog, or straight from the panel's date field */
  | { kind: "due" }
  | { kind: "setDue"; due: string | null };

/** Where the card is drawn, as far as its controls care. */
export interface CardPlace {
  /** the project whose board is open, and this is its card */
  here?: boolean;
  /** the card a panel is showing */
  inPanel?: boolean;
  /** shown in place under a row on its parent's card */
  nested?: boolean;
  /** on the board it lives on (its project's board, or the main board for an item in none) */
  onItsBoard?: boolean;
  /** "Copy to my board" is offered: OEMUAP or someone else's personal board, and the viewer has a board of their own */
  copyOut?: boolean;
  /** the card is on a personal board: its owners may link it to OEMUAP (the OEMUAP group) */
  personal?: boolean;
  /** the card is on OEMUAP, which has GitHub (docs/github.md): the GitHub group, a project's settings */
  github?: boolean;
}

export interface HeadIcon {
  key: "panel" | "board" | "plus" | "link";
  icon: IconName;
  /** the tooltip, on hover and on keyboard focus */
  tip: string;
  /** its accessible name */
  label: string;
  action: CardAction;
}

export interface Primary {
  /** what it does next, for the colour: developed, deployed, verified, done, reopen */
  to: "developed" | "deployed" | "verified" | "done" | "reopen";
  label: string;
  title: string;
  disabled: boolean;
  action: CardAction;
}

/** A menu entry; `action: "assign"` opens the assignee picker (the avatar's). `off`: shown greyed, with why. */
export interface MenuEntryModel {
  key: string;
  label: string;
  hint?: string;
  off?: string;
  action: CardAction | "assign";
}
export interface MenuGroupModel {
  title: "Work" | "Organize" | "Admin" | "Next step" | "My board" | "OEMUAP" | "GitHub";
  entries: MenuEntryModel[];
}

export interface CardModel {
  icons: HeadIcon[];
  primary: Primary | null;
  /** the split primary's ▾ (the deploy and verify queues): Send back */
  split: MenuGroupModel[] | null;
  /** Agree / Question chips; `reactOff`: shown, but the viewer can't react */
  react: boolean;
  reactOff: boolean;
  /** the Claim chip: only while nobody has the item */
  claim: boolean;
  /** the bottom row's note control (the composer, or the Note chip) */
  note: boolean;
  /** a project has no bottom row: its progress is in its head */
  bar: boolean;
  menu: MenuGroupModel[];
}

/** "03-01", "Project 03", "#188": how the card names itself in labels and tooltips. */
export function cardModel(it: BoardItem, viewer: Viewer, place: CardPlace, ref: string): CardModel {
  const { here = false, inPanel = false, nested = false, onItsBoard = false, copyOut = false, personal = false, github = false } = place;
  const subItem = !!it.parent && it.parent.kind !== "project";
  const acts = kindActions(it.kind, subItem);
  const has = (a: KindAction) => acts.has(a);
  const flow = kindFlow(it.kind);
  const project = flow === "none";
  const live = !it.archived;
  const closed = it.status === "closed";
  const lane = flowLane(it);
  // items take sub-items once someone has classified them (or they have some already)
  const takesChildren = !!it.kind || childCount(it) > 0;
  const creates = live && can(viewer, "create") && can(viewer, "organize");
  const mayLink = live && has("link") && can(viewer, "organize");
  // closing a project is for leads (design 4.11)
  const mayClose = has("close") && can(viewer, "close") && (flow !== "none" || can(viewer, "project"));
  const mayStage = has("stage") && live && can(viewer, "stage");

  // -- the head icons, always in this order: ⧉ ↗ + link (… is drawn after them)
  const icons: HeadIcon[] = [];
  if (has("openPanel") && !inPanel && !nested && !here)
    icons.push({ key: "panel", icon: "panel", tip: "Open in panel", label: `Open ${ref} in a panel to the right`, action: { kind: "panel", num: it.num } });
  if (project) {
    if (has("openBoard") && !here && live) icons.push({ key: "board", icon: "goto", tip: "Open board", label: `Open the ${it.title} board`, action: { kind: "openProject" } });
  } else if (!inPanel && !nested && !onItsBoard && live) {
    const where = it.project ? `the ${it.project.title} board` : "the main board";
    icons.push({ key: "board", icon: "goto", tip: it.project ? "Go to its board" : "Go to it on the main board", label: `Go to ${ref} on ${where}`, action: { kind: "goto", num: it.num } });
  }
  if (project ? has("newItemHere") && creates : has("addSubItem") && creates && takesChildren)
    icons.push({
      key: "plus",
      icon: "plus",
      tip: project ? "New item here" : "Add sub-item",
      label: project ? `Log a new item in ${ref}` : `Log a new item under ${ref}`,
      action: { kind: "addChild" },
    });
  if (mayLink) icons.push({ key: "link", icon: "link", tip: "Add link", label: `Link ${ref} to another item`, action: { kind: "link" } });

  // -- the primary: the next step of its flow
  let primary: Primary | null = null;
  let split: MenuGroupModel[] | null = null;
  const sendBack: MenuEntryModel = { key: "sendback", label: "Send back to To do…", hint: "Not fixed: say what's still wrong", action: { kind: "stage", to: "todo" } };
  // steps nobody marked: a lead or a verifier moves it on anyway, and the dialog asks who did them
  const mayVerify = has("verify") && live && can(viewer, "verify");
  const recordsFor = can(viewer, "stage_for");
  const notMarked = (to: "deployed" | "verified") => missingSteps(it, to);
  const skipTo = (to: "deployed" | "verified"): MenuEntryModel => ({
    key: `skip-${to}`,
    label: to === "deployed" ? "Mark deployed…" : "Verified…",
    hint: `Nobody marked it ${notMarked(to).join(" or ")}: say who did`,
    action: { kind: "stage", to },
  });
  const skips: MenuEntryModel[] = [];
  if (flow === "fix" && !closed && recordsFor) {
    if (lane === "todo" && mayStage) skips.push(skipTo("deployed"));
    if ((lane === "todo" || lane === "deploy") && mayVerify) skips.push(skipTo("verified"));
  }
  /** why a step can't be taken by this viewer because an earlier one was never marked (null: it can) */
  const unmarked = (to: "deployed" | "verified") => {
    const missing = notMarked(to);
    return missing.length && !recordsFor ? `Nobody marked it ${missing.join(" or ")}: a lead or a verifier can record who did it` : null;
  };
  if (flow === "fix") {
    if (closed) {
      if (mayClose) primary = { to: "reopen", label: closeLabel(it.kind, it.status), title: "Move this back to Open", disabled: false, action: { kind: "reopen" } };
    } else if (lane === "todo" && mayStage) {
      primary = { to: "developed", label: "Mark developed", title: "The fix is done - put it in the deploy queue", disabled: false, action: { kind: "stage", to: "developed" } };
    } else if (lane === "deploy" && mayStage) {
      const why = unmarked("deployed");
      primary = {
        to: "deployed",
        label: "Mark deployed",
        title: why ?? (notMarked("deployed").length ? "It's live - say who developed it, and put it in the verify queue" : "It's live - put it in the verify queue"),
        disabled: !!why,
        action: { kind: "stage", to: "deployed" },
      };
      split = [{ title: "Next step", entries: [sendBack, ...skips] }];
    } else if (lane === "verify" && mayVerify) {
      const why = unmarked("verified");
      // a lead or a verifier names the developer in the dialog, so the second-person rule is checked there
      const ok = !why && (notMarked("verified").length > 0 || canVerify(viewer, it));
      primary = {
        to: "verified",
        label: "Verified",
        title:
          why ??
          (!ok
            ? "You marked this developed, so someone else checks it"
            : notMarked("verified").length
              ? "You checked it and it works - say who developed it, then close it and move it to the archive"
              : "You checked it and it works - close it and move it to the archive"),
        disabled: !ok,
        action: { kind: "stage", to: "verified" },
      };
      if (mayStage) split = [{ title: "Next step", entries: [sendBack] }];
    }
  } else if (flow === "done" && mayClose) {
    primary = {
      to: closed ? "reopen" : "done",
      label: closeLabel(it.kind, it.status),
      title: closed ? "Not done after all - back to To do" : "It's done - tasks go To do → Done, with no deploy or verify queue",
      disabled: false,
      action: { kind: closed ? "reopen" : "close" },
    };
  }

  // -- the "…" menu
  const work: MenuEntryModel[] = [];
  if (mayStage && (lane === "deploy" || lane === "verify")) work.push(sendBack);
  work.push(...skips);
  // every card with a bottom row has the composer or the Note chip there; a project has none
  if (project && has("note") && can(viewer, "note")) work.push({ key: "note", label: "Add a note", action: { kind: "note" } });
  if (has("attach") && can(viewer, "attach")) work.push({ key: "attach", label: "Add screenshot", hint: "Or paste or drop it on the card", action: { kind: "attach" } });
  if (has("assign") && canAssignItem(viewer, assigneesOf(it).map((a) => a.id)))
    work.push({ key: "assign", label: "Assign…", hint: it.assignee ? `With ${nameList(assigneeNames(it))} now · the avatar opens this too` : "The avatar opens this too", action: "assign" });
  if (flow === "fix" && mayClose && !closed) work.push({ key: "close", label: closeLabel(it.kind, it.status), hint: "Resolved without the fix flow", action: { kind: "close" } });
  const organize: MenuEntryModel[] = [];
  if (canEditItem(viewer, it))
    organize.push({ key: "edit", label: "Edit title and description…", hint: "The history keeps the old wording", action: { kind: "edit" } });
  // drafts (OEM-286): whoever logged it, and leads
  if (canSetDraft(viewer, it))
    organize.push(
      it.draft
        ? { key: "draft", label: "Ready for the team", hint: "Take it out of drafts: everyone sees it on the board", action: { kind: "draft", value: false } }
        : { key: "draft", label: "Make draft", hint: "Not ready to discuss yet: hidden from others until it's ready", action: { kind: "draft", value: true } },
    );
  if (live && canSetDue(viewer, it))
    organize.push({ key: "due", label: it.due ? "Change due date…" : "Set due date…", hint: it.due ? `Due ${it.due} now; or clear it` : "The day it should be done", action: { kind: "due" } });
  if (has("organize") && can(viewer, "organize"))
    organize.push({ key: "organize", label: "Organize…", hint: project ? "Kind, a board of its own" : "Kind, project, parent", action: { kind: "organize" } });
  if (has("history")) organize.push({ key: "history", label: "History", hint: "Everything that happened to it", action: { kind: "history" } });
  // a project's repos and how they deploy (docs/github.md): OEMUAP's leads
  const company = github && viewer.org?.kind !== "personal";
  if (project && company && can(viewer, "project")) organize.push({ key: "projectSettings", label: "Project settings…", hint: "Repos, and how each shows a deploy", action: { kind: "projectSettings" } });
  const admin: MenuEntryModel[] = [];
  if (has("nondev") && can(viewer, "nondev"))
    admin.push({ key: "nondev", label: it.nondev ? "Back to dev" : "Non-dev", hint: it.nondev ? "Back on the developers' list" : "On the board, off the developers' list", action: { kind: "nondev", value: !it.nondev } });
  if (has("teamsres") && can(viewer, "teamsres"))
    admin.push({
      key: "teamsres",
      label: it.teamsResolved ? "Not via Teams" : "Resolved via Teams",
      hint: it.teamsResolved ? "Clear the Teams mark" : "The fix was agreed in a Teams thread or call",
      action: { kind: "teamsres", value: !it.teamsResolved },
    });
  if (project && mayClose)
    admin.push({ key: "close", label: closed ? "Reopen project" : closeLabel(it.kind, it.status), hint: "Its items stay where they are", action: { kind: closed ? "reopen" : "close" } });
  if (has("archive") && can(viewer, "archive"))
    admin.push(
      !live
        ? { key: "archive", label: "Restore to board", hint: "Put it back on the board", action: { kind: "archive", value: false } }
        : closed
          ? { key: "archive", label: "Archive", hint: "Off the board, still searchable", action: { kind: "archive", value: true } }
          : { key: "archive", label: "Archive", off: `Once it is ${flow === "done" ? "done" : "closed"}`, action: { kind: "archive", value: true } },
    );
  // an item into the viewer's own board: from someone else's personal board (orgs.md 1.5: owners and editors,
  // a frozen board too), or from OEMUAP (6.2: anyone, linked to it so they can send updates)
  const fromCompany = viewer.org?.kind !== "personal";
  const mine: MenuEntryModel[] =
    copyOut && can(viewer, "copy_out") && !project
      ? [{ key: "copy", label: "Copy to my board…", hint: fromCompany ? "A private copy there, linked to this one; nothing changes here" : "A new item there, saying where it came from", action: { kind: "copyToMine" } }]
      : [];
  // a personal item and OEMUAP (orgs.md 6): the board's owners only; a company item never opens here
  const oemuap: MenuEntryModel[] = [];
  if (personal && !project && live && can(viewer, "bridge")) {
    if (it.bridge) {
      oemuap.push({ key: "send", label: `Send updates${it.bridge.waiting ? ` · ${it.bridge.waiting}` : ""}…`, hint: `One note on OEMUAP ${it.bridge.label}, which you review first`, action: { kind: "bridgeSend" } });
      oemuap.push({ key: "unbridge", label: `Unlink from OEMUAP ${it.bridge.label}`, hint: "Notes already sent stay there", action: { kind: "bridgeUnlink" } });
    } else {
      oemuap.push({ key: "bridge", label: "Link to an OEMUAP item…", hint: "Nothing changes on OEMUAP", action: { kind: "bridgeLink" } });
      oemuap.push({ key: "clone", label: "Create an OEMUAP item from this…", hint: "Prefilled; you edit it before it goes", action: { kind: "bridgeCreate" } });
    }
  }
  // GitHub (docs/github.md): OEMUAP's items only (a personal board has no code); copying is for anyone
  const gh: MenuEntryModel[] = [];
  if (company && !project) {
    const ref = githubRef(it.num);
    gh.push({ key: "copyBranch", label: "Copy branch name", hint: branchName(it), action: { kind: "copyText", text: branchName(it), what: "the branch name" } });
    gh.push({ key: "copyRef", label: "Copy reference", hint: `${ref}: for a PR title or a commit`, action: { kind: "copyText", text: ref, what: "the reference" } });
    gh.push({ key: "copyPr", label: "Copy for PR description", hint: `${ref} and a link back here`, action: { kind: "copyText", text: ref, what: "the PR description", pr: true } });
    if (live && can(viewer, "github")) {
      gh.push({ key: "linkPr", label: "Link a PR by URL…", hint: "For a PR that doesn't name it", action: { kind: "linkPr" } });
      for (const pr of it.prs ?? []) gh.push({ key: `unlinkPr-${pr.id}`, label: `Unlink PR ${pr.number}`, hint: pr.repo, action: { kind: "unlinkPr", pr } });
    }
  }
  const menu = (
    [
      ["Work", work],
      ["Organize", organize],
      ["GitHub", gh],
      ["OEMUAP", oemuap],
      ["My board", mine],
      ["Admin", admin],
    ] as const
  )
    .filter(([, xs]) => xs.length)
    .map(([title, entries]) => ({ title, entries }));

  return {
    icons,
    primary,
    split,
    react: has("react"),
    reactOff: !can(viewer, "react"),
    claim: has("claim") && can(viewer, "claim") && !it.assignee && live && !closed,
    note: !project && has("note") && can(viewer, "note"),
    bar: !project,
    menu,
  };
}
